import React, { useState } from "react";
import { Modal, Box, Typography, IconButton, Avatar, TextField, FormControl, MenuItem, Button, CircularProgress } from "@mui/material";
import { Close } from "@mui/icons-material";
import { textFieldSx } from "../../../styles/GlobalStyles";

const UpdateAssistantModal = ({
    open,
    handleClose,
    assistantDetails,
    logo,
    handleLogoUpload,
    assistantName,
    setAssistantName,
    assistantType,
    setAssistantType,
    assistantCategory,
    setAssistantCategory,
    otherCategory,
    setOtherCategory,
    assistantDescription,
    setAssistantDescription,
    validationError,
    setValidationError,
    loading,
    setLoading,
    handleAssistantProfileUpdate,
}) => {
    const [OtherCategoryError, setOtherCategoryError] = useState(false);
    
    const handleOtherCategoryBlur = () => {
        if (assistantCategory === "Other" && (!otherCategory || otherCategory.trim() === "")) {
            setOtherCategoryError(true);
        }
    };

    const handleOtherCategoryFocus = () => {
        setOtherCategoryError(false);
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="update-assistant-modal"
            aria-describedby="modal-to-update-assistant-profile"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 480,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                borderRadius: '16px',
                p: 4,
                outline: 'none',
            }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography id="update-assistant-modal" variant="h6" component="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                        Update Assistant Profile
                    </Typography>
                    <IconButton onClick={handleClose} size="small" sx={{ p: 0 }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                {/* Logo Upload */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                    <input accept="image/*" style={{ display: 'none' }} id="logo-upload" type="file" onChange={handleLogoUpload} />
                    <label htmlFor="logo-upload">
                        <IconButton component="span">
                            <Avatar alt={assistantDetails?.Name} src={logo} sx={{ width: 80, height: 80 }} />
                        </IconButton>
                    </label>
                </Box>

                {/* Form Fields */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        label="Assistant Name"
                        fullWidth
                        variant="outlined"
                        value={assistantName}
                        onChange={(e) => setAssistantName(e.target.value)}
                        size="small"
                        sx={textFieldSx}
                    />
                    <FormControl fullWidth size="small">
                        <TextField
                            label="Assistant Type"
                            select
                            value={assistantType}
                            onChange={(e) => setAssistantType(e.target.value)}
                            sx={textFieldSx}
                            size="small"
                        >
                            <MenuItem value="inbound">Inbound</MenuItem>
                            <MenuItem value="outbound">Outbound</MenuItem>
                        </TextField>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <TextField
                            label="Assistant Category"
                            select
                            value={assistantCategory}
                            onChange={(e) => {
                                setAssistantCategory(e.target.value);
                                // Reset other category if not "Other"
                                if (e.target.value !== "Other") {
                                    setOtherCategory("");
                                }
                            }}
                            sx={textFieldSx}
                            size="small"
                        >
                            <MenuItem value="Sales">Sales</MenuItem>
                            <MenuItem value="Customer Support">Customer Support</MenuItem>
                            <MenuItem value="IT Support">IT Support</MenuItem>
                            <MenuItem value="Human Resources">Human Resources</MenuItem>
                            <MenuItem value="Healthcare / Service Industry">Healthcare / Service Industry</MenuItem>
                            <MenuItem value="Banking Support">Banking Support</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </TextField>
                    </FormControl>
                    
                    {/* Other Category field - shows only when "Other" is selected */}
                    {assistantCategory === "Other" && (
                        <TextField
                            label="Specify Other Category"
                            fullWidth
                            variant="outlined"
                            value={otherCategory}
                            onChange={(e) => setOtherCategory(e.target.value)}
                            size="small"
                            required
                            error={OtherCategoryError}
                            onBlur={handleOtherCategoryBlur}
                            onFocus={handleOtherCategoryFocus}
                            helperText={OtherCategoryError ? "Please specify the category" : ""}
                            sx={textFieldSx}
                        />
                    )}
                    
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        variant="outlined"
                        maxRows={4}
                        value={assistantDescription}
                        onChange={(e) => {
                            const value = e.target.value;
                            setAssistantDescription(value);
                            setValidationError(value.length < 50);
                        }}
                        size="small"
                        error={validationError}
                        helperText={validationError ? "Description must be at least 50 characters long." : ""}
                        sx={textFieldSx}
                    />
                    <Button
                        variant="contained"
                        onClick={() => {
                            // Validate other category before proceeding
                            if (assistantCategory === "Other" && (!otherCategory || otherCategory.trim() === "")) {
                                setOtherCategoryError(true);
                                return;
                            }
                            setLoading(true);
                            handleAssistantProfileUpdate();
                        }}
                        fullWidth
                        disabled={validationError || loading || (assistantCategory === "Other" && (!otherCategory || otherCategory.trim() === ""))}
                        sx={{
                            bgcolor: "#010320",
                            color: "#FFF",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "10px 16px",
                            "&:hover": { bgcolor: "#010320" },
                            "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
                        }}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: "#FFF" }} /> : "Save Changes"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default UpdateAssistantModal;
